import YouTubeIframeLoader from 'youtube-iframe';
import Vimeo from '@vimeo/player';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import domReady from '../helpers/dom-ready';

class Lightbox {
  static init() {
    return new Lightbox();
  }

  constructor() {
    this.bodyBackgroundColor = getComputedStyle(document.documentElement)
      .getPropertyValue('--color-body-background');

    this.lightboxButtons = document.querySelectorAll('.lightbox-button');
    this.footer = document.querySelector('footer');

    this.isLightboxOpen = false;

    if (this.lightboxButtons) {
      [...this.lightboxButtons].forEach((lightboxButton) => {
        lightboxButton.addEventListener('click', (event) => {
          this.createLightbox(event);
        });
      });
    }

    const closeIconSrc = `<svg width="71" height="71" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="35.3309" cy="35.3309" r="35" fill="${this.bodyBackgroundColor}"/><path d="M11.8132 35.0845h46.5426m4.1888 0c0 15.1658-12.2943 27.4601-27.4601 27.4601S7.6244 50.2503 7.6244 35.0845 19.9187 7.6244 35.0845 7.6244s27.4601 12.2943 27.4601 27.4601Z" stroke="#111111" stroke-width="2"/></svg>`;
    const expandIconSrc = `<svg width="71" height="71" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="35.3309" cy="35.3309" r="35" fill="${this.bodyBackgroundColor}"/><path d="M11.8133 35.0846h46.5426M35.0846 11.8133v46.5426m27.4601-23.2713c0 15.1658-12.2943 27.4601-27.4601 27.4601s-27.46-12.2943-27.46-27.4601 12.2942-27.46 27.46-27.46 27.4601 12.2942 27.4601 27.46Z" stroke="#111111" stroke-width="2"/></svg>`;
    this.expandIcon = encodeURIComponent(expandIconSrc.replace(/"/g, "'"));
    this.closeIcon = encodeURIComponent(closeIconSrc.replace(/"/g, "'"));
  }

  createLightbox(event) {
    const {
      contentType,
    } = event.target.dataset;

    this.lightbox = document.createElement('section');

    if (contentType === 'video') {
      this.lightbox.classList.add('grid-wrapper', 'lightbox');
    } else {
      this.lightbox.classList.add('lightbox');
    }

    const background = document.createElement('div');
    background.classList.add('background');

    this.contentWrapper = document.createElement('div');

    if (contentType === 'video') {
      this.contentWrapper.classList.add('content-wrapper', 'grid');
    } else {
      this.contentWrapper.classList.add('content-wrapper');
    }

    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.classList.add('close');
    closeButton.innerHTML = '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M12 12.7 1.3 23.43l-.72-.71L11.3 12 .6 1.3l.7-.72L12 11.3 22.7.6l.72.7L12.7 12l10.7 10.7-.7.72L12 12.7Z" fill="currentColor" stroke-width="2"/></svg>';

    this.content = document.createElement('div');
    if (contentType === 'video') {
      this.content.classList.add('content', 'grid-col', '-col-start-sm-1', '-col-start-md-2', '-col-start-lg-3', '-col-start-xl-3', '-col-span-sm-12', '-col-span-md-10', '-col-span-lg-8', '-col-span-xl-8');
    } else {
      this.content.classList.add('content');
    }

    this.contentWrapper.appendChild(closeButton);
    this.contentWrapper.appendChild(this.content);

    this.lightbox.appendChild(background);
    this.lightbox.appendChild(this.contentWrapper);

    this.footer.appendChild(this.lightbox);

    this.addLightboxContent(event);
  }

  addEventListeners() {
    if (this.lightbox) {
      const closeButton = this.lightbox.querySelector('.close');
      closeButton.addEventListener('click', () => {
        this.toggleIsLightboxOpen();
      });

      if (this.image) {
        this.image.addEventListener('click', () => {
          this.toggleIsImageInFullscreen();
        });
      }
    }
  }

  addLightboxContent(event) {
    const {
      contentType, caption, imageSrc, imageAlt, videoType, videoId, videoSrc, videoMime,
    } = event.target.dataset;

    if (contentType === 'image') {
      if (imageSrc) {
        this.image = document.createElement('img');
        this.image.src = imageSrc;
        this.image.alt = imageAlt;
        this.image.classList.add('image');

        this.content.appendChild(this.image);
      }
    }

    if (contentType === 'video') {
      if (videoType === 'youtube') {
        this.video = document.createElement('div');
        this.video.classList.add('video', '-embed', '-fade-in');

        this.player = document.createElement('div');
        this.player.classList.add('player');

        this.video.appendChild(this.player);
        this.content.appendChild(this.video);

        this.initYoutube(videoId);
      }

      if (videoType === 'vimeo') {
        this.video = document.createElement('div');
        this.video.classList.add('video', '-embed', '-fade-in');

        this.player = document.createElement('iframe');
        this.player.classList.add('player');
        this.player.src = `https://player.vimeo.com/video/${videoId}?autoplay=1`;

        this.video.appendChild(this.player);
        this.content.appendChild(this.video);
      }

      if (videoType === 'medialibrary' && videoSrc) {
        const video = document.createElement('div');
        video.classList.add('video', '-fade-in');
        const player = document.createElement('video');
        player.classList.add('player');
        player.controls = true;
        player.autoplay = true;
        const source = document.createElement('source');
        source.src = `${videoSrc}#t=0.1`;

        if (videoMime) {
          source.type = videoMime;
        }

        player.appendChild(source);
        video.appendChild(player);
        this.content.appendChild(video);
      }
    }

    if (caption) {
      const captionElement = document.createElement('div');
      captionElement.classList.add('caption-wrapper');

      const paragraph = document.createElement('p');
      paragraph.classList.add('caption');
      paragraph.innerHTML = caption;

      captionElement.appendChild(paragraph);
      this.contentWrapper.appendChild(captionElement);
    }

    this.addEventListeners();

    this.toggleIsLightboxOpen();
  }

  toggleIsLightboxOpen() {
    this.isLightboxOpen = !this.isLightboxOpen;

    if (this.image) {
      if (this.image.classList.contains('-expanded')) {
        this.image.classList.remove('-expanded');
      }

      this.image.style.cursor = `url("data:image/svg+xml,${this.expandIcon}") 35 35, auto`;
      this.image.classList.add('-fade-in');
    }

    if (this.isLightboxOpen) {
      disableBodyScroll(this.lightbox);
    } else {
      enableBodyScroll(this.lightbox);
      this.footer.removeChild(this.lightbox);
    }
  }

  toggleIsImageInFullscreen() {
    this.image.classList.toggle('-expanded');
    this.content.classList.toggle('-expanded');

    if (this.image.classList.contains('-expanded')) {
      this.image.style.cursor = `url("data:image/svg+xml,${this.closeIcon}") 35 35, auto`;
    } else {
      this.image.style.cursor = `url("data:image/svg+xml,${this.expandIcon}") 35 35, auto`;
    }
    this.image.classList.remove('-fade-in');
  }

  initYoutube(videoId) {
    const { video } = this;

    const player = video.querySelector('.player');

    if (player && videoId) {
      YouTubeIframeLoader.load((YT) => {
        this.youtube = new YT.Player(player, {
          height: '100%',
          width: '100%',
          videoId,
          events: {
            onReady: () => {
              this.iframe = video.querySelector('iframe');
              this.youtube.playVideo();
            },
          },
          playerVars: {
            start: 0,
            controls: 0, // Show pause/play buttons in player
            showinfo: 0, // Hide the video title
            modestbranding: 1, // Hide the Youtube Logo
            fs: 0, // Hide the full screen button
            cc_load_policy: 0, // Hide closed captions
            iv_load_policy: 3, // Hide the Video Annotations
            autohide: 0,
            rel: 0, // Hide video controls when playing
            disablekb: 1, // disable the keyboard controls
            autoplay: 1, // autoplay video
          },
        });
      });
    }
  }

  initVimeo() {
    this.iframe = this.video.querySelector('iframe');

    const player = this.video.querySelector('.player');
    if (player) {
      let isReady = false;
      const vimeo = new Vimeo(player);
      vimeo.ready().then(() => {
        vimeo.setCurrentTime(0);
      });
      vimeo.on('play', () => {
        if (!isReady) {
          isReady = true;
          this.onReady();
        }
        vimeo.on('timeupdate', (data) => {
          if (data.percent > 0) {
            vimeo.off('timeupdate');
            this.paused = false;
          }
        });
      });
      vimeo.on('pause', () => {
        vimeo.off('timeupdate');
        this.paused = true;
      });
      vimeo.getPaused().then((paused) => {
        if (!paused) {
          if (!isReady) {
            isReady = true;
            this.onReady();
          }
        } else {
          vimeo.off('timeupdate');
        }
        this.paused = paused;
      });
      this.vimeo = vimeo;
    }
  }
}

domReady(() => {
  Lightbox.init();
});
